import * as React from 'react';
import type {
  BulkActionCounts,
  BulkActionStatus,
} from '@web-config-app/core-react';
import { bulkActionOperations } from '@web-config-app/core';
import { Box } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { ProgressBar } from '../progress-bar/progress-bar.component';
import {
  StatusSummary,
  type StatusSummaryFilterStatus,
} from '../status-summary/status-summary.component';

export type BulkActionReportingStatus = Exclude<BulkActionStatus, 'idle'>;
type BulkActionReportingOperationType = (typeof bulkActionOperations)[number];

type BulkActionReportingProgressBarBaseProps = {
  status: Exclude<BulkActionStatus, 'idle'>;
  mode?: React.ComponentProps<typeof ProgressBar>['mode'];
  processedCount: number;
  totalCount: number;
  counts: BulkActionCounts;
  progressBarId?: string;
  ariaLive?: 'polite' | 'assertive';
  onStatusFilterChange?: (status: StatusSummaryFilterStatus | null) => void;
  activeStatusFilter?: StatusSummaryFilterStatus | null;
};

type BulkActionReportingProgressBarImportExportProps =
  BulkActionReportingProgressBarBaseProps & {
    operationType: 'import' | 'export';
    entityLabel: string;
    entityLabelPlural: string;
  };

type BulkActionReportingProgressBarOtherOperationProps =
  BulkActionReportingProgressBarBaseProps & {
    operationType: Exclude<
      BulkActionReportingOperationType,
      'import' | 'export'
    >;
    entityLabel?: never;
    entityLabelPlural?: never;
  };

export type BulkActionReportingProgressBarProps =
  | BulkActionReportingProgressBarImportExportProps
  | BulkActionReportingProgressBarOtherOperationProps;

const operationTextMap: Record<
  BulkActionReportingOperationType,
  {
    progressLabelId: string;
    activeVerbId: string;
    doneMessageId: string;
    /**
     * When set, this message is used instead of `doneMessageId` when the
     * operation completes with failures. Used by operations that halt on
     * failure and don't persist partial results (e.g. export).
     */
    failedAtMessageId?: string;
  }
> = {
  import: {
    progressLabelId: 'BULK_ACTION_REPORTING_PROGRESS_LABEL_IMPORT',
    activeVerbId: 'BULK_ACTION_REPORTING_PROGRESS_ACTION_IMPORTING',
    doneMessageId: 'BULK_ACTION_REPORTING_PROGRESS_DONE_IMPORTING',
  },
  export: {
    progressLabelId: 'BULK_ACTION_REPORTING_PROGRESS_LABEL_EXPORT',
    activeVerbId: 'BULK_ACTION_REPORTING_PROGRESS_ACTION_EXPORTING',
    doneMessageId: 'BULK_ACTION_REPORTING_PROGRESS_DONE_EXPORTING',
    failedAtMessageId: 'BULK_ACTION_REPORTING_PROGRESS_FAILED_AT_MESSAGE',
  },
  copyToEnvironment: {
    progressLabelId: 'BULK_ACTION_REPORTING_PROGRESS_LABEL_COPY',
    activeVerbId: 'BULK_ACTION_REPORTING_PROGRESS_ACTION_COPYING',
    doneMessageId: 'BULK_ACTION_REPORTING_PROGRESS_DONE_COPYING',
  },
  clone: {
    progressLabelId: 'BULK_ACTION_REPORTING_PROGRESS_LABEL_DUPLICATION',
    activeVerbId: 'BULK_ACTION_REPORTING_PROGRESS_ACTION_DUPLICATING',
    doneMessageId: 'BULK_ACTION_REPORTING_PROGRESS_DONE_DUPLICATING',
  },
  publish: {
    progressLabelId: 'BULK_ACTION_REPORTING_PROGRESS_LABEL_PUBLISH',
    activeVerbId: 'BULK_ACTION_REPORTING_PROGRESS_ACTION_PUBLISHING',
    doneMessageId: 'BULK_ACTION_REPORTING_PROGRESS_DONE_PUBLISHING',
  },
  archive: {
    progressLabelId: 'BULK_ACTION_REPORTING_PROGRESS_LABEL_ARCHIVE',
    activeVerbId: 'BULK_ACTION_REPORTING_PROGRESS_ACTION_ARCHIVING',
    doneMessageId: 'BULK_ACTION_REPORTING_PROGRESS_DONE_ARCHIVING',
  },
  delete: {
    progressLabelId: 'BULK_ACTION_REPORTING_PROGRESS_LABEL_DELETE',
    activeVerbId: 'BULK_ACTION_REPORTING_PROGRESS_ACTION_DELETING',
    doneMessageId: 'BULK_ACTION_REPORTING_PROGRESS_DONE_DELETING',
  },
};

const progressStatusByBulkStatus = {
  loading: 'active',
  success: 'success',
  error: 'error',
} as const;

const resolveEntityLabels = ({
  operationType,
  formatMessage,
  entityLabel,
  entityLabelPlural,
}: {
  operationType: BulkActionReportingOperationType;
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
  entityLabel?: string;
  entityLabelPlural?: string;
}) => {
  if (operationType === 'import' || operationType === 'export') {
    return {
      singular:
        entityLabel ??
        formatMessage({
          id: 'BULK_ACTION_REPORTING_PROGRESS_ENTITY_SINGULAR',
        }),
      plural:
        entityLabelPlural ??
        formatMessage({
          id: 'BULK_ACTION_REPORTING_PROGRESS_ENTITY_PLURAL',
        }),
    };
  }

  return {
    singular: formatMessage({
      id: 'BULK_ACTION_REPORTING_PROGRESS_ENTITY_SINGULAR',
    }),
    plural: formatMessage({
      id: 'BULK_ACTION_REPORTING_PROGRESS_ENTITY_PLURAL',
    }),
  };
};

const getProgressLabel = (
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
  operationText: (typeof operationTextMap)[BulkActionReportingOperationType],
  status: BulkActionReportingStatus,
  isComplete: boolean,
) => {
  if (status === 'success') {
    return formatMessage({ id: 'BULK_ACTION_REPORTING_PROGRESS_COMPLETE' });
  }
  if (status === 'error') {
    if (operationText.failedAtMessageId) {
      return formatMessage({ id: 'BULK_ACTION_REPORTING_PROGRESS_ERROR' });
    }
    if (isComplete) {
      return formatMessage({
        id: 'BULK_ACTION_REPORTING_PROGRESS_COMPLETE_WITH_ERRORS',
      });
    }
    return formatMessage({ id: 'BULK_ACTION_REPORTING_PROGRESS_ERROR' });
  }

  return formatMessage({ id: operationText.progressLabelId });
};

const getPrimaryMessage = ({
  formatMessage,
  operationText,
  status,
  resolvedEntityLabel,
  resolvedEntityLabelPlural,
  processedCount,
  totalCount,
  succeededCount,
  isComplete,
}: Pick<
  BulkActionReportingProgressBarBaseProps,
  'status' | 'processedCount' | 'totalCount'
> & {
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
  operationText: (typeof operationTextMap)[BulkActionReportingOperationType];
  isComplete: boolean;
  succeededCount: number;
  resolvedEntityLabel: string;
  resolvedEntityLabelPlural: string;
}) => {
  const activeVerb = formatMessage({ id: operationText.activeVerbId });

  if (status === 'success') {
    return formatMessage(
      { id: operationText.doneMessageId },
      {
        count: totalCount,
        entityLabel: resolvedEntityLabel,
        entityLabelPlural: resolvedEntityLabelPlural,
        strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
      },
    );
  }

  if (status === 'error') {
    // Operations that halt on failure (export) always show "Stopped at X of Y"
    // using the succeeded count, regardless of whether all items were processed.
    if (operationText.failedAtMessageId) {
      return formatMessage(
        { id: operationText.failedAtMessageId },
        {
          succeededCount,
          totalCount,
          entityLabel: resolvedEntityLabel,
          entityLabelPlural: resolvedEntityLabelPlural,
          strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
        },
      );
    }

    // Other operations (copy, clone) process all items even when some fail,
    // so they show the done message when complete or "Stopped at" when interrupted.
    if (isComplete) {
      return formatMessage(
        { id: operationText.doneMessageId },
        {
          count: totalCount,
          entityLabel: resolvedEntityLabel,
          entityLabelPlural: resolvedEntityLabelPlural,
          strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
        },
      );
    }

    return formatMessage(
      { id: 'BULK_ACTION_REPORTING_PROGRESS_STOPPED_AT_MESSAGE' },
      {
        entityLabel: resolvedEntityLabel,
        processedCount,
        totalCount,
        strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
      },
    );
  }

  return formatMessage(
    { id: 'BULK_ACTION_REPORTING_PROGRESS_ACTIVE_MESSAGE' },
    {
      action: activeVerb,
      entityLabel: resolvedEntityLabel,
      processedCount,
      totalCount,
      strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
    },
  );
};

/**
 * Progress + status summary for long-running bulk operations.
 *
 * Use `mode="indeterminate"` when the total is not yet known (for example, while
 * preparing or fetching needed data, then switch to `mode="determinate"` once
 * `totalCount` is available so progress and counts can advance in lockstep.
 *
 * For `operationType="import" | "export"`, provide `entityLabel` and
 * `entityLabelPlural`; other operation types intentionally use generic entity copy.
 */
export const BulkActionReportingProgressBar = ({
  operationType,
  status,
  mode = 'determinate',
  processedCount,
  totalCount,
  counts,
  entityLabel,
  entityLabelPlural,
  progressBarId,
  ariaLive,
  onStatusFilterChange,
  activeStatusFilter,
}: BulkActionReportingProgressBarProps) => {
  const { formatMessage } = useIntl();
  const resolvedAriaLive =
    ariaLive ?? (status === 'error' ? 'assertive' : 'polite');
  const normalizedTotalCount = Math.max(totalCount, 0);
  const clampedProcessedCount = Math.min(
    Math.max(processedCount, 0),
    normalizedTotalCount,
  );
  const isIndeterminate = mode === 'indeterminate';
  const progressMax = normalizedTotalCount > 0 ? normalizedTotalCount : 1;
  const operationText = operationTextMap[operationType];
  // For operations that halt on failure (export), the progress bar
  // should reflect only the succeeded count, not total processed.
  const effectiveProcessedCount =
    status === 'error' && operationText.failedAtMessageId
      ? Math.min(Math.max(counts.succeeded, 0), normalizedTotalCount)
      : clampedProcessedCount;
  const isComplete =
    normalizedTotalCount > 0 && effectiveProcessedCount >= normalizedTotalCount;
  const resolvedUnprocessedCount =
    typeof counts.unprocessed === 'number' && counts.unprocessed > 0
      ? counts.unprocessed
      : undefined;
  const { singular: resolvedEntityLabel, plural: resolvedEntityLabelPlural } =
    resolveEntityLabels({
      operationType,
      formatMessage,
      entityLabel,
      entityLabelPlural,
    });

  const helperInfo = (
    <Box
      data-testid="bulk-action-reporting-live-region"
      aria-live={resolvedAriaLive}
      aria-atomic="true"
      css={{
        opacity: isIndeterminate ? 0 : 1,
        transition: 'opacity $transitions$defaultTime ease-out',
      }}
    >
      <StatusSummary
        primaryMessage={getPrimaryMessage({
          formatMessage,
          operationText,
          status,
          resolvedEntityLabel,
          resolvedEntityLabelPlural,
          processedCount: effectiveProcessedCount,
          totalCount: normalizedTotalCount,
          succeededCount: counts.succeeded,
          isComplete,
        })}
        status={status === 'error' ? 'error' : undefined}
        successCount={counts.succeeded}
        skippedCount={counts.skipped}
        failedCount={counts.error}
        unprocessedCount={resolvedUnprocessedCount}
        onStatusFilterChange={onStatusFilterChange}
        activeStatusFilter={activeStatusFilter}
      />
    </Box>
  );

  if (isIndeterminate) {
    return (
      <ProgressBar
        id={progressBarId}
        mode="indeterminate"
        label={getProgressLabel(
          formatMessage,
          operationText,
          status,
          isComplete,
        )}
        status={progressStatusByBulkStatus[status]}
        helperInfo={helperInfo}
      />
    );
  }

  return (
    <ProgressBar
      id={progressBarId}
      mode="determinate"
      label={getProgressLabel(formatMessage, operationText, status, isComplete)}
      value={effectiveProcessedCount}
      min={0}
      max={progressMax}
      status={progressStatusByBulkStatus[status]}
      helperInfo={helperInfo}
    />
  );
};
